import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, Platform, TouchableOpacity, StyleSheet } from 'react-native';
import { getApiHost } from '@api/constant';

interface BackendStatusIndicatorProps {
  theme: boolean;
  appVersion?: string;
}

interface HealthResponse {
  status: string;
  timestamp: string;
  uptime?: number;
  port?: number;
  environment?: string;
  version?: string;
}

type ConnectionStatus = 'connected' | 'disconnected' | 'checking';

const STATUS_COLORS: Record<ConnectionStatus, string> = {
  connected: '#10B981',
  disconnected: '#EF4444',
  checking: '#F59E0B',
};

const webClassName = (className: string) =>
  (Platform.OS === 'web' ? ({ className } as any) : {});

async function resolveHealthUrl(): Promise<string> {
  if (Platform.OS === 'web') {
    const base =
      process.env.EXPO_PUBLIC_WEB_API ||
      (typeof window !== 'undefined' ? window.location.origin : '');
    return `${base.replace(/\/$/, '')}/api/health`;
  }

  const apiHost = await getApiHost();
  return `${apiHost.replace(/\/$/, '')}/api/health`;
}

const BackendStatusIndicator: React.FC<BackendStatusIndicatorProps> = ({ theme, appVersion }) => {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('checking');
  const [showTooltip, setShowTooltip] = useState(false);
  const [healthData, setHealthData] = useState<HealthResponse | null>(null);
  const checkedRef = useRef(false);

  const applyHealthResult = useCallback((nextStatus: ConnectionStatus, nextHealth: HealthResponse | null) => {
    setConnectionStatus((prev) => (prev === nextStatus ? prev : nextStatus));
    setHealthData((prev) => {
      if (nextHealth == null) return prev === null ? prev : null;
      if (
        prev &&
        prev.status === nextHealth.status &&
        prev.version === nextHealth.version &&
        prev.environment === nextHealth.environment &&
        prev.uptime === nextHealth.uptime
      ) {
        return prev;
      }
      return nextHealth;
    });
  }, []);

  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;

    let cancelled = false;

    const checkBackendHealth = async () => {
      try {
        const healthUrl = await resolveHealthUrl();
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await fetch(healthUrl, {
          method: 'GET',
          signal: controller.signal,
          headers: { 'Content-Type': 'application/json' },
        });

        clearTimeout(timeoutId);
        if (cancelled) return;

        if (response.ok) {
          const data: HealthResponse = await response.json();
          applyHealthResult('connected', data);
        } else {
          applyHealthResult('disconnected', null);
        }
      } catch (error) {
        if (cancelled) return;
        console.warn('Backend health check failed:', error);
        applyHealthResult('disconnected', null);
      }
    };

    void checkBackendHealth();

    if (Platform.OS === 'web') {
      return () => {
        cancelled = true;
      };
    }

    const interval = setInterval(() => {
      void checkBackendHealth();
    }, 30000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [applyHealthResult]);

  const statusColor = STATUS_COLORS[connectionStatus];

  const formatUptime = (seconds?: number) => {
    if (!seconds) return '';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const tooltipText = (() => {
    const parts: string[] = [];
    if (appVersion) parts.push(`Frontend: v${appVersion}`);
    if (connectionStatus === 'connected' && healthData) {
      parts.push(`Status: ${healthData.status}`);
      if (healthData.version) parts.push(`Backend: v${healthData.version}`);
      if (healthData.environment) parts.push(`Env: ${healthData.environment}`);
      if (healthData.uptime) parts.push(`Uptime: ${formatUptime(healthData.uptime)}`);
    } else {
      parts.push(`Status: ${connectionStatus}`);
    }
    return parts.join('\n');
  })();

  return (
    <View
      {...webClassName('login-backendStatus')}
      style={styles.container}
      pointerEvents="box-none"
    >
      <View style={styles.row}>
        <View style={styles.dotWrap}>
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel="Backend connection status"
            onPress={() => setShowTooltip((open) => !open)}
            style={[styles.dot, { backgroundColor: statusColor }]}
            {...webClassName('login-backendStatusDot')}
          />

          {showTooltip ? (
            <View
              style={[
                styles.tooltip,
                {
                  backgroundColor: theme ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
                },
              ]}
            >
              <Text
                style={[
                  styles.tooltipText,
                  { color: theme ? '#F9FAFB' : '#1F2937' },
                ]}
              >
                {tooltipText}
              </Text>
            </View>
          ) : null}
        </View>

        {appVersion ? (
          <Text
            style={[
              styles.versionText,
              { color: theme ? '#6B7280' : '#9CA3AF' },
            ]}
          >
            v{appVersion}
          </Text>
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  dotWrap: {
    position: 'relative',
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
      default: {
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.8,
        shadowRadius: 4,
        elevation: 4,
      },
    }),
  },
  tooltip: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    borderRadius: 8,
    padding: 8,
    minWidth: 180,
    ...Platform.select({
      web: {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
        elevation: 8,
      },
    }),
  },
  tooltipText: {
    fontSize: 11,
    fontFamily: 'monospace',
    lineHeight: 16,
  },
  versionText: {
    fontSize: 11,
    fontWeight: '500',
    lineHeight: 12,
    ...Platform.select({
      web: {
        display: 'flex',
        alignItems: 'center',
      },
      default: {},
    }),
  },
});

export default memo(BackendStatusIndicator);
